import {
	Component,
	EventEmitter,
	Input,
	Output,
	ViewChild
}                            from '@angular/core';
import { BaseComponent }     from '@core/bases/base-component/base.component';
import {
	IBaseHttpParams,
	RequestActionEnum,
	RequestCompleteListState,
	RequestState
}                            from '@core/interfaces/api.interface';
import { ENTITY_TYPE }       from '@core/interfaces/entity.interface';
import { NgSelectComponent } from '@ng-select/ng-select';
import {
	FilterValueType,
	IActiveFilterData,
	IFilter,
	IFiltersWithAsyncInit,
	ISelectFilterable,
	SelectFilterModeEnum
}                            from '@shared/list-operations/model/filter.model';
import {
	IResetEvent,
	ListOperationsResetModeEnum
}                            from '@shared/list-operations/model/list-operations.model';
import {
	Observable,
	of,
	Subject
}                            from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	finalize,
	switchMap,
	take,
	takeUntil,
	tap
}                            from 'rxjs/operators';

@Component( {
	templateUrl: './select-filter-base.component.html',
	styleUrls: [ './select-filter-base.component.scss' ]
} )
export class SelectFilterBaseComponent extends BaseComponent implements ISelectFilterable {

	private _resetEvent$: Observable<IResetEvent>;

	private _filterIsInitialized: boolean;

	private _defaultFilterData: IFilter;

	private _restoreValue: string;

	private _defaultTooltipOpenDelay: 300;
	private _tooltipTimeout: ReturnType<typeof setTimeout>;
	private _blockEmit: boolean;

	protected selectFilterMode: SelectFilterModeEnum = SelectFilterModeEnum.REQUESTED_LIST;

	protected onChangeCallback: (
		filterType: FilterValueType,
		searchRestoreValueKey: string,
		paramKey: string
	) => IActiveFilterData;

	searchRequestCallback: (
		searchRequestParams: IBaseHttpParams
	) => Observable<RequestCompleteListState<ENTITY_TYPE>>;

	selectedItem: string;

	filterItems: ENTITY_TYPE[];

	filterInput$: Subject<string> = new Subject<string>();

	filterLoading = false;


	get defaultFilterData(): IFilter {
		return this._defaultFilterData;
	}

	@Input() set defaultFilterData( defaultFilterData: IFilter ) {
		this._defaultFilterData = defaultFilterData;

		// set default value
		if ( defaultFilterData?.selectFilter?.search ) {
			this.setFilter( defaultFilterData?.selectFilter?.search );
			this.init();
		} else {
			this.init();
			if ( !this._filterIsInitialized ) {
				this.filterIsInitialized.emit( this.createFiltersAsyncInitState() );

			} else {
				this.onChange( null );
			}
		}
	}

	@Input() set shouldReset( resetEvent$: Observable<IResetEvent> ) {
		if ( !this._resetEvent$ ) {
			this._resetEvent$ = resetEvent$;
		} else {
			return;
		}

		// TODO will be replaced by store resetState selector
		this._resetEvent$
		.pipe( takeUntil( this.ngUnsubscribe ) )
		.subscribe( ( resetEvent: IResetEvent ) => {

			// TODO service property will be obsolete and replaced by array for individual filterIdentifiers of a (currently not finally specified)
			//  IListOperationsReset interface
			if ( resetEvent && resetEvent.service === this.defaultFilterData.service ) {

				// on change is not called if no selected values exist, thus _blockEmit will not set back to false again
				// therefor _blockEmit is set here to true only by this condition where selected values exist
				this._blockEmit = resetEvent?.blockEmit && !!this.ngSelectComponent.selectedValues?.length;

				switch ( resetEvent.resetMode ) {
					case ListOperationsResetModeEnum.CLEAR:
						this.ngSelectComponent.handleClearClick();
						/*
						 * should actually de-focus select element since it is focused by
						 * 'handleClearClick()' -- de-focusing works but does not prevent
						 * down-scrolling (-_-)
						 *
						 * implementation of 'ngSelectComponent.handleClearClick()' according
						 * to https://dev.to/jwp/the-ng-select-javascript-1cgm:
						 *
						 * handleClearClick() {
						 *  if (this.hasValue) {
						 *  this.itemsList.clearSelected(true);
						 *    this._updateNgModel();
						 *  }
						 *  this._clearSearch();
						 *  this.focus();                           // <--  causes scrolling
						 *  if (this._isTypeahead) {
						 *    this.typeahead.next(null);
						 *  }
						 *  this.clearEvent.emit();
						 *  this._onSelectionChanged();
						 * }
						 *
						 * for work-around see comment in
						 * 'ListOperationsSidebarComponent.ngAfterViewChecked()'
						 */
						this.ngSelectComponent.blur();
						break;

					case ListOperationsResetModeEnum.DEFAULT:
					case ListOperationsResetModeEnum.RESTORE:
						this.resetFilterValue( resetEvent.resetMode );
						break;
				}
			}
		} );
	}

	@Output() change: EventEmitter<IActiveFilterData> = new EventEmitter();

	@Output() filterIsInitialized: EventEmitter<IFiltersWithAsyncInit> = new EventEmitter();

	@ViewChild( NgSelectComponent ) ngSelectComponent: NgSelectComponent;

	constructor() {
		super();
	}

	private getStaticListItems(): ENTITY_TYPE[] {
		return this.defaultFilterData?.selectFilter?.items ?? [];
	}

	private setFilterItems( entities: ENTITY_TYPE[] ): void {
		this.filterItems = entities;
		if ( entities?.length ) {
			this.selectedItem = entities[ 0 ][ this.defaultFilterData?.bindLabel ];
			this.onChange( entities[ 0 ] );
		}

		// for filter initialization only
		if ( !this._filterIsInitialized ) {
			this.filterIsInitialized.emit( this.createFiltersAsyncInitState() );
		}
	}

	private createFiltersAsyncInitState(): IFiltersWithAsyncInit {
		this._filterIsInitialized = true;
		return {
			filterId: this.defaultFilterData?.id,
			isInitialized: true
		};
	}

	private setFilter( searchValue: string ) {

		switch ( this.selectFilterMode ) {
			case SelectFilterModeEnum.REQUESTED_LIST:
				this.searchRequest( of( searchValue ) )
				.pipe(
					takeUntil( this.ngUnsubscribe ),
					take( 1 )
				)
				.subscribe( ( entities: ENTITY_TYPE[] ) => {
					this.setFilterItems( entities );
				} );
				break;

			case SelectFilterModeEnum.STATIC_LIST:
				this.setFilterItems( this.getStaticListItems() );
				break;
		}
	}

	private searchRequest( searchValue$: Observable<string> ): Observable<ENTITY_TYPE[]> {
		return searchValue$.pipe(
			tap( () => { this.filterLoading = true; } ),
			switchMap( ( searchValue: string ) => {
				if ( !searchValue ) {
					const requestCompleteListState: RequestCompleteListState<ENTITY_TYPE> = {
						action: RequestActionEnum.LIST,
						httpResponse: undefined,
						paging: null,
						requestState: RequestState.NOT_ASKED,
						list: []
					};
					return of( requestCompleteListState );
				}

				const searchActiveFilterKey =
					this.defaultFilterData?.selectFilter?.searchActiveFilterKey;

				const searchRequestParams: IBaseHttpParams =
					searchActiveFilterKey
					? {
							...this.defaultFilterData?.selectFilter?.searchRequestParams,
							activeFilters: {
								...this.defaultFilterData?.selectFilter?.searchRequestParams?.activeFilters,
								[ searchActiveFilterKey ]: { param: [ searchValue ] }
							}
						}
					: {
							...this.defaultFilterData?.selectFilter?.searchRequestParams,
							search: [ searchValue ]
						};

				return this.searchRequestCallback( searchRequestParams );
			} ),
			switchMap( ( result: RequestCompleteListState<ENTITY_TYPE> ) => {
				return of( result?.list );
			} ),
			finalize( () => { this.filterLoading = false; } )
		);
	}

	private resetFilterValue( resetMode ): void {
		switch ( resetMode ) {
			// TODO actually resets should use corresponding IOperators objects of configData
			case ListOperationsResetModeEnum.DEFAULT:
				this.setFilter( this.defaultFilterData?.selectFilter?.search );
				break;
			case ListOperationsResetModeEnum.RESTORE:
				this.setFilter( this._restoreValue );
				break;
		}
	}

	protected getNotAskedRequestCompleteListState<T>(): RequestCompleteListState<T> {
		return {
			action: RequestActionEnum.LIST,
			httpResponse: undefined,
			paging: null,
			requestState: RequestState.NOT_ASKED,
			list: []
		};
	}

	protected init(): void {
		if ( !this._filterIsInitialized ) {

			switch ( this.selectFilterMode ) {
				case SelectFilterModeEnum.REQUESTED_LIST:
					// subscribe to input changes
					this.filterInput$
					.pipe(
						debounceTime( 200 ),
						distinctUntilChanged(),
						switchMap( ( searchValue: string ) => this.searchRequest( of( searchValue ) ) )
					)
					.subscribe( ( entities: ENTITY_TYPE[] ) => {
						this.filterItems = entities;
					} );
					break;

				case SelectFilterModeEnum.STATIC_LIST:
					this.filterItems = this.getStaticListItems();
					this._filterIsInitialized = true;
					break;
			}
		}
	}

	onChange( entity: ENTITY_TYPE ) {
		let param: string;

		if ( entity && entity[ this.defaultFilterData?.selectFilter?.paramKey ] ) {
			param = entity[ this.defaultFilterData?.selectFilter?.paramKey ] ?? null;
		}

		if ( entity && entity[ this.defaultFilterData?.selectFilter?.searchRestoreValueKey ] ) {
			this._restoreValue = entity[ this.defaultFilterData?.selectFilter?.searchRestoreValueKey ] ?? null;
		}

		const activeFilterData: IActiveFilterData = {
			param,
			restoreValue: this._restoreValue
		};

		if ( !this._blockEmit ) {
			this.change.emit( activeFilterData );
		}

		this._blockEmit = false;
	}

	changeTooltipVisibility( tooltip, isVisible?: boolean, entity?: ENTITY_TYPE ): void {

		if ( this._tooltipTimeout ) {
			clearTimeout( this._tooltipTimeout );
			this._tooltipTimeout = null;
		}

		if ( isVisible && entity && entity[ this.defaultFilterData?.tooltip?.bindKey ] ) {
			this._tooltipTimeout = setTimeout( () => {
				clearTimeout( this._tooltipTimeout );
				tooltip.open();
			}, this.defaultFilterData?.tooltip?.openDelay ?? this._defaultTooltipOpenDelay );
			return;
		}

		tooltip.close();
	}
}
